import { useCallback, useState } from 'react';
import { getClientSongCollectionSongStats, TrndsClientSongTiktokStats } from '@round/api';
import useAbortableEffect from '../../../../Hooks/useAbortableEffect';

export type UseClientSongCollectionSongsParams = Omit<
    Parameters<typeof getClientSongCollectionSongStats>[0],
    'songCollectionId'
> & {
    songCollectionId: number | undefined;
};

export default function useClientSongCollectionSongs({
    clientId,
    songCollectionId,
    page,
    page_size,
    ordering,
    search,
}: UseClientSongCollectionSongsParams) {
    const [isLoading, setIsLoading] = useState(false);
    const [hasErrorLoadingStats, setHasErrorLoadingStats] = useState(false);
    const [count, setCount] = useState(0);
    const [songs, setSongs] = useState<TrndsClientSongTiktokStats[]>([]);

    const fetchData = useCallback(
        async (requestInit?: RequestInit) => {
            if (!songCollectionId) {
                return;
            }

            try {
                setIsLoading(true);
                setHasErrorLoadingStats(false);
                const response = await getClientSongCollectionSongStats(
                    {
                        clientId,
                        songCollectionId,
                        page,
                        page_size,
                        ordering,
                        search,
                    },
                    requestInit
                );

                if (response.status === 200) {
                    setSongs(response.data.results);
                    setCount(response.data.count);
                    return;
                }

                setHasErrorLoadingStats(true);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setHasErrorLoadingStats(true);
            } finally {
                setIsLoading(false);
            }
        },
        [clientId, ordering, page, page_size, search, songCollectionId]
    );

    useAbortableEffect(
        (signal) => {
            fetchData({ signal });
        },
        [fetchData]
    );

    return {
        count,
        songs,
        isLoading,
        hasErrorLoadingStats,
        reset: () => {
            setCount(0);
            setSongs([]);
            setIsLoading(false);
            setHasErrorLoadingStats(false);
        },
        refresh: fetchData,
    };
}
